/*
 * Do NOT import components that use hooks in this file or the static directory.
 * This file is rendered with ReactDOMServer, and hooks are not supported.
 */
import { FAX_TYPE_REQUEST, LOGO_MASTER } from "core/consts";
import { getName } from "core/model/accounts";
import { Account, Auction, GetOntologyType } from "core/types";
import { RecareNameLogo } from "ds_legacy/components/RecareLogo";
import { TEXT_DARK_PRIMARY } from "ds_legacy/materials/colors";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  FONT_FAMILY,
  FONT_SIZE_15,
  FONT_SIZE_20,
  FONT_SIZE_28,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
} from "ds_legacy/materials/typography";
import { TranslationComposition } from "translations/helpers";
import Translations from "translations/types";

const bodyStyle = ({
  bold = false,
  color,
  lineHeight,
  marginProp,
}: {
  bold?: boolean;
  color?: string;
  large?: boolean;
  lineHeight?: string;
  marginProp?: string;
}) => {
  return {
    color: color || TEXT_DARK_PRIMARY,
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE_15,
    fontWeight: bold ? FONT_WEIGHT_BOLD : FONT_WEIGHT_REGULAR,
    lineHeight: lineHeight || "2",
    margin: marginProp || margin(0, 2),
  };
};

const subheadingStyle = {
  color: TEXT_DARK_PRIMARY,
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE_20,
  fontWeight: FONT_WEIGHT_BOLD,
  lineHeight: "1.5",
  margin: margin(2),
};

const InformationSlug = ({ info, label }: { info: string; label: string }) => {
  return info ? (
    <div
      style={{
        alignItems: "stretch",
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
      }}
    >
      <span
        style={{
          ...bodyStyle({
            lineHeight: "1.5",
            marginProp: margin(0, 0, 0, 2),
          }),
        }}
      >
        {label}
      </span>
      <span
        style={{
          ...bodyStyle({
            lineHeight: "1.5",
            marginProp: margin(0, 2, 0, 0.32),
          }),
        }}
      >
        {info}
      </span>
    </div>
  ) : null;
};

const Recarelogo = ({
  patientId,
  translations,
}: {
  patientId: string;
  translations: Translations;
}) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        objectFit: "contain",
        padding: padding(1, 2, 1, 2),
      }}
    >
      <RecareNameLogo
        noLazyLoad
        style={{ height: dp(50), position: "relative" }}
        variant={LOGO_MASTER}
      />
      <p
        style={{
          ...bodyStyle({
            bold: true,
            marginProp: margin(0),
          }),
        }}
      >
        {translations.faxRequest.recareId({ patientId })}
      </p>
    </div>
  );
};

const Header = ({
  isRequest,
  patientId,
  senderName,
  translations,
}: {
  isRequest: boolean;
  patientId: string;
  senderName: string;
  translations: Translations;
}) => {
  return (
    <>
      <div
        style={{
          color: "#212121",
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE_28,
          fontWeight: FONT_WEIGHT_BOLD,
          lineHeight: 1.1,
          margin: margin(2, 2, 3, 2),
        }}
      >
        {isRequest
          ? translations.faxRequest.titleNew
          : translations.faxRequest.coverPage.titlePatientValidation}
      </div>
      <div style={subheadingStyle}>
        {translations.faxRequest.fromSender({
          clinicName: senderName,
        })}
      </div>
      <div style={{ lineHeight: "1.5", margin: margin(0, 2) }}>
        <div
          style={{
            ...bodyStyle({
              marginProp: margin(0),
            }),
          }}
        >
          <p
            style={{
              ...bodyStyle({
                marginProp: margin(0, 0, 1),
              }),
            }}
          >
            {translations.faxRequest.faxIntroSalutation}
          </p>
          <p
            style={{
              ...bodyStyle({
                marginProp: margin(0, 0, 1),
              }),
            }}
          >
            {isRequest
              ? translations.faxRequest.faxIntroBodyOne({ patientId })
              : translations.faxRequest.coverPage.infoBodyTextValidation}
          </p>
        </div>
        {isRequest ? (
          <p
            style={{
              ...bodyStyle({
                marginProp: margin(0, 0, 1),
              }),
            }}
          >
            {translations.faxRequest.faxIntroBodyTwo}
          </p>
        ) : (
          <TranslationComposition
            translations={
              translations.faxRequest.coverPage.infoTextPatientValidation
            }
            withOptions
          >
            {([before, , after]) => (
              <span
                style={{
                  ...bodyStyle({
                    marginProp: margin(0),
                  }),
                }}
              >
                {before}
                <span
                  style={{
                    ...bodyStyle({
                      bold: true,
                      marginProp: margin(0),
                    }),
                  }}
                >
                  {patientId}
                </span>
                {after}
              </span>
            )}
          </TranslationComposition>
        )}
      </div>
    </>
  );
};

const SubHeading = ({
  senderName,
  translations,
}: {
  senderName: string;
  translations: Translations;
}) => {
  return (
    <>
      <div style={subheadingStyle}>
        {translations.faxRequest.coverPage.hospitalInformation.title}
      </div>
      <div
        style={{
          ...bodyStyle({
            bold: true,
            large: true,
          }),
        }}
      >
        {senderName}
      </div>
    </>
  );
};

const ContactBox = ({
  contact,
  contactPhone = "",
  getOntology,
  senderName,
  translations,
}: {
  contact: Account | undefined;
  contactPhone: string | undefined;
  getOntology: GetOntologyType;
  senderName: string;
  translations: Translations;
}) => {
  const contactPerson = getName(contact, getOntology);
  const email = contact?.email || "";

  return (
    <div
      style={{
        margin: margin(4, 0),
      }}
    >
      <SubHeading senderName={senderName} translations={translations} />
      <InformationSlug
        info={contactPerson}
        label={
          translations.faxRequest.coverPage.hospitalInformation.contactPerson
        }
      />
      <InformationSlug
        info={contactPhone}
        label={
          translations.faxRequest.coverPage.hospitalInformation.telephoneNumber
        }
      />
      <InformationSlug
        info={email}
        label={translations.faxRequest.coverPage.hospitalInformation.email}
      />
    </div>
  );
};

const PageAligner = ({ children }: { children: any }) => {
  return (
    <div
      style={{
        padding: padding(2, 0, 0, 0),
      }}
    >
      {children}
    </div>
  );
};

const MainAligner = ({ children }: { children: any }) => {
  return (
    <div
      style={{
        margin: margin(0, 2),
      }}
    >
      {children}
    </div>
  );
};

export function StaticFaxCoverLetter({
  auction,
  faxType,
  getOntology,
  translations,
}: {
  auction: Auction;
  faxType: number;
  getOntology: GetOntologyType;
  translations: Translations;
}) {
  const isRequest = faxType === FAX_TYPE_REQUEST;
  const patientId = auction?.patient?.user_id || "";
  const socialWorkerContact = auction?.patient?.social_worker;

  const senderName = auction?.patient?.careseeker?.name || "";

  return (
    <PageAligner>
      <MainAligner>
        <Recarelogo patientId={patientId} translations={translations} />
        <Header
          isRequest={isRequest}
          patientId={patientId}
          senderName={senderName}
          translations={translations}
        />
        <ContactBox
          contact={socialWorkerContact}
          contactPhone={socialWorkerContact?.phone}
          getOntology={getOntology}
          senderName={senderName}
          translations={translations}
        />
      </MainAligner>
    </PageAligner>
  );
}
